import {
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
} from "@mui/material";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDataProvider, useTranslate } from "react-admin";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTableSearchParams } from "tanstack-table-search-params";
import { FilterBar } from "./FilterBar";
import styles from "./List.module.scss";
import { SearchBar } from "./SearchBar";

const mapObjectKeys = (originalObject, keyMapping = {}) => {
	const mappedObject = {};
	for (const key in originalObject) {
		if (Object.prototype.hasOwnProperty.call(originalObject, key)) {
			const newKey = keyMapping[key] || key;
			mappedObject[newKey] = originalObject[key];
		}
	}
	return mappedObject;
};

export const List = ({
	dataset,
	title,
	perPageDefault = 10,
	rowsPerPageOptions = [10, 25, 50, 100],
	Children = null,
	search,
	globalFilters = [],
	display = false,
	showSearch = false,
	searchPlaceholder = null,
	emptyText = "",
	disableSyncWithLocation = false,
	onUpdateTotal,
	onUpdateGlobalFilters,
}) => {
	const translate = useTranslate();
	const navigate = useNavigate();
	const [query] = useSearchParams();
	const { pathname } = useLocation();
	const dataProvider = useDataProvider();
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState(search);
	const [data, setData] = useState([]);
	const [total, setTotal] = useState(0);

	const defaultSort = useMemo(() => {
		const defaultSortColumn = dataset.columns?.find(
			(column) => column?.defaultSorting,
		);

		return defaultSortColumn
			? [
					{
						id: defaultSortColumn.accessorKey,
						desc: defaultSortColumn.defaultSorting === "desc",
					},
				]
			: [];
	}, [dataset.columns]);

	const defaultFilters = useMemo(() => {
		const defaultFilters = dataset.columns?.map((column) => {
			if (column?.meta?.filter) {
				return {
					id: column.accessorKey,
					value: column.meta.filter.defaultValue,
				};
			}
			return null;
		});
		return defaultFilters?.filter(Boolean);
	}, [dataset.columns]);

	const stateAndOnChanges = useTableSearchParams(
		{ query, pathname, replace: (url) => navigate(url, { replace: true }) },
		{
			paramNames: {
				sorting: "sort",
				pagination: {
					pageIndex: "page",
					pageSize: "perPage",
				},
				columnFilters: "filters",
			},
			defaultValues: {
				pagination: {
					pageIndex: 0,
					pageSize: perPageDefault || 10,
				},
				sorting: defaultSort || [],
				columnFilters: defaultFilters || [],
			},
		},
	);

	const table = useReactTable({
		columns: dataset.columns,
		data: data,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		manualFiltering: true,
		enableSorting: true,
		enableSortingRemoval: false,
		...stateAndOnChanges,
	});

	const handleRowClick = useCallback(
		(event, item) => {
			if (event.button !== 0 || event.ctrlKey || event.metaKey) {
				return;
			}
			event.preventDefault();
			const url =
				dataset.rowRedirect?.(item) ||
				`/parliament/${dataset.key}/${item.uid || item.id}`;

			navigate(url, { replace: dataset.disablePushToHistoryOnRowClick });
		},
		[dataset, navigate],
	);

	const fetch = (isTotal = false) => {
		if (isTotal) setLoading(false);
		else setLoading(true);

		const sort = table.getState().sorting[0] || {};
		const columnFilters = table.getState().columnFilters;

		const dataFilters = {
			...columnFilters?.reduce((acc, f) => {
				if ((f.key || f.id) && f.value !== null) {
					acc[f.key || f.id] = f.value;
				}
				return acc;
			}, {}),
			...mapObjectKeys(globalFilters, dataset.filterMapping),
		};

		return dataProvider
			.getList(dataset.key, {
				filter: dataFilters,
				pagination: {
					page: isTotal ? 1 : table.getState().pagination.pageIndex + 1,
					perPage: isTotal ? 1 : table.getState().pagination.pageSize,
				},
				include: dataset.include,
				sort: isTotal
					? {}
					: { field: sort.id, order: sort.desc ? "desc" : "asc" },
				search: isTotal ? search : searchQuery,
				...(dataset.searchTable && {
					searchTable: dataset.searchTable,
				}),
				...(dataset.searchLanguage && {
					searchLanguage: dataset.searchLanguage,
				}),
				...(dataset.distinct && { distinct: dataset.distinct }),
			})
			.then((res) => {
				if (res) {
					if (!isTotal) setData(res.data || []);
					setTotal(res.total ?? 0);
					onUpdateTotal?.(dataset.key, res.total ?? 0);
				}
				if (!isTotal) setLoading(false);
			})
			.catch((error) => {
				console.error(
					`Failed to fetch ${dataset.key} ${isTotal ? "total" : "data"}:`,
					error,
				);
				if (!isTotal) setData([]);
				setTotal(0);
				onUpdateTotal?.(dataset.key, 0);
				if (!isTotal) setLoading(false);
			});
	};

	useEffect(() => {
		setSearchQuery(search);
	}, [search]);

	useEffect(() => {
		if (display) {
			if (table.getState().pagination.pageIndex !== 0) {
				table.setPageIndex(0);
			} else {
				fetch();
			}
		}
	}, [
		JSON.stringify(table.getState().sorting),
		JSON.stringify(table.getState().columnFilters),
		searchQuery,
		JSON.stringify(globalFilters),
	]);

	useEffect(() => {
		if (display) {
			fetch();
		}
	}, [
		display,
		table.getState().pagination.pageIndex,
		table.getState().pagination.pageSize,
	]);

	useEffect(() => {
		if (!display) {
			fetch(true);
		}
	}, [searchQuery, JSON.stringify(globalFilters)]);

	if (!display) return null;

	return (
		<div className={styles.container}>
			<div className={styles.actionBar}>
				{dataset.columns?.map((c) => c.meta?.filter).length > 0 && (
					<FilterBar
						title={title}
						columnFilters={table.getState().columnFilters}
						globalFilters={globalFilters}
						columns={dataset.columns}
						onFilterChange={(columnId, value) => {
							table.setColumnFilters((prev) => [
								...prev.filter((f) => f.id !== columnId),
								...(value !== null ? [{ id: columnId, value }] : []),
							]);
						}}
					/>
				)}
				{showSearch && (
					<SearchBar
						onSearchChange={(query) => setSearchQuery(query)}
						placeholder={searchPlaceholder}
						styles={{
							width: "max-content !important",
						}}
						size="small"
						disableSyncWithLocation={disableSyncWithLocation}
						onUpdateGlobalFilters={onUpdateGlobalFilters}
					/>
				)}
			</div>
			<TableContainer
				component={Paper}
				sx={{ width: "100%", minHeight: "40em", boxShadow: "none" }}
			>
				<Table>
					{loading ? (
						<div className={styles.loader}>
							<CircularProgress />
						</div>
					) : (
						<>
							{!Children && (
								<TableHead className={styles.tableHeader}>
									{table.getHeaderGroups().map((headerGroup) => (
										<TableRow
											className={styles.tableHeaderRow}
											key={headerGroup.id}
										>
											{headerGroup.headers
												.filter(
													(header) =>
														header.column.columnDef.isVisible !== false,
												)
												.map((header) => (
													<TableCell
														key={header.id}
														className={styles.tableHeaderCell}
														sx={{
															cursor:
																!searchQuery && header.column.getCanSort()
																	? "pointer"
																	: "default",
														}}
													>
														{header.isPlaceholder ? null : (
															<TableSortLabel
																active={
																	!searchQuery &&
																	header.column.getCanSort() &&
																	header.column.getIsSorted()
																}
																hideSortIcon
																direction={header.column.getIsSorted() || "asc"}
																onClick={!searchQuery && header.column.getCanSort() ? header.column.getToggleSortingHandler(): null}
																sx={{
																	cursor:
																		!searchQuery && header.column.getCanSort()
																			? "pointer"
																			: "default",
																}}
															>
																{flexRender(
																	header.column.columnDef.header,
																	header.getContext(),
																)}
															</TableSortLabel>
														)}
													</TableCell>
												))}
										</TableRow>
									))}
								</TableHead>
							)}
							{!data || data.length === 0 ? (
								<TableBody>
									<TableRow className={styles.emptyList}>
										<TableCell colSpan={dataset.columns?.length || 1}>
											{emptyText || "Pas de résultats"}
										</TableCell>
									</TableRow>
								</TableBody>
							) : Children ? (
								<TableBody>
									{data.map((item, rowIndex) => (
										<Children
											key={item.id || item.uid || item.hash}
											resource={item}
											index={rowIndex}
										/>
									))}
								</TableBody>
							) : (
								<TableBody className={styles.tableBody}>
									{table.getRowModel().rows.map((row) => {
										return (
											<TableRow
												key={row.id}
												className={styles.tableRow}
												component="a"
												href={
													dataset.rowRedirect
														? `#${dataset.rowRedirect(row.original)}`
														: `#/parliament/${dataset.key}/${
																row.original.uid || row.original.id
															}`
												}
												onClick={(e) => handleRowClick(e, row.original)}
											>
												{row
													.getVisibleCells()
													.filter(
														(cell) => cell.column.columnDef.isVisible !== false,
													)
													.map((cell) => (
														<TableCell
															key={cell.id}
															className={styles.tableCell}
														>
															{flexRender(
																cell.column.columnDef.cell,
																cell.getContext(),
															)}
														</TableCell>
													))}
											</TableRow>
										);
									})}
								</TableBody>
							)}
							{data.length > 0 && (
								<TableFooter className={styles.tableFooter}>
									<TableRow align="left" className={styles.paginationRow}>
										<TablePagination
											page={table.getState().pagination.pageIndex}
											count={total ?? 0}
											rowsPerPage={table.getState().pagination.pageSize}
											rowsPerPageOptions={rowsPerPageOptions}
											onPageChange={(_, newPage) => table.setPageIndex(newPage)}
											onRowsPerPageChange={(e) =>
												table.setPageSize(Number(e.target.value))
											}
											labelDisplayedRows={({ from, to, count }) => {
												return `${from}-${to} ${translate(
													"pos.main.from_to_pagination",
												)} ${count}`;
											}}
										/>
									</TableRow>
								</TableFooter>
							)}
						</>
					)}
				</Table>
			</TableContainer>
		</div>
	);
};
